import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, labelize } from "@/lib/constants";
import { Plus, PaperPlaneTilt, Trash, Play } from "@phosphor-icons/react";

const FREQUENCIES = ["hourly", "daily", "weekly", "monthly"];

export default function ScheduledReports() {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    name: "Daily digest",
    frequency: "daily",
    recipient_emails: "",
    report_type: "dashboard_summary",
    is_active: true,
  });

  const load = async () => {
    const { data } = await api.get("/scheduled-reports");
    setRows(data.reports);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        recipient_emails: form.recipient_emails
          .split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.post("/scheduled-reports", payload);
      toast.success("Scheduled");
      setShowNew(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const runNow = async (id) => {
    try {
      const { data } = await api.post(`/scheduled-reports/${id}/run`);
      toast.success(`Ran — ${data.status}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this scheduled report?")) return;
    await api.delete(`/scheduled-reports/${id}`);
    toast.success("Deleted"); load();
  };

  const toggle = async (r) => {
    await api.patch(`/scheduled-reports/${r.id}`, { is_active: !r.is_active });
    toast.success(r.is_active ? "Paused" : "Resumed");
    load();
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-mono">Automations</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">Scheduled reports</h1>
          <div className="mt-1 text-sm text-zinc-600">Email digests via Resend. Scheduler cadence: 60s.</div>
        </div>
        <button onClick={() => setShowNew((v) => !v)} data-testid="new-report-btn"
          className="bg-black text-white border border-black px-3 py-2 text-xs uppercase tracking-widest hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100">
          <Plus size={12} className="inline mr-1" /> {showNew ? "Close" : "New report"}
        </button>
      </div>

      {showNew && (
        <form onSubmit={submit} className="brut-card p-5 mb-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="report-form">
          <div>
            <label className="label-mono">Name</label>
            <input data-testid="report-name" className="w-full border border-black px-3 py-2 mt-1"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="label-mono">Frequency</label>
            <select data-testid="report-frequency" className="w-full border border-black px-2 py-1.5 mt-1"
              value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
              {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label-mono">Recipient emails (comma-separated)</label>
            <input data-testid="report-recipients" className="w-full border border-black px-3 py-2 mt-1"
              value={form.recipient_emails} onChange={(e) => setForm({ ...form, recipient_emails: e.target.value })}
              placeholder="Leave blank to default to your account email" />
          </div>
          <div className="md:col-span-2 flex items-center justify-between border-t border-black pt-3">
            <label className="inline-flex items-center gap-2 text-xs">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <span className="uppercase tracking-widest">Start active</span>
            </label>
            <button type="submit" data-testid="report-submit"
              className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold">Schedule</button>
          </div>
        </form>
      )}

      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm font-mono min-w-[900px]">
          <thead>
            <tr className="border-b border-black text-[10px] uppercase tracking-widest">
              <th className="py-2 px-4 text-left">Name</th>
              <th className="py-2 px-4 text-left">Frequency</th>
              <th className="py-2 px-4 text-left">Recipients</th>
              <th className="py-2 px-4 text-left">Next run</th>
              <th className="py-2 px-4 text-left">Last run</th>
              <th className="py-2 px-4 text-left">Status</th>
              <th className="py-2 px-4 text-left">Active</th>
              <th className="py-2 px-4 text-left"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-zinc-200" data-testid={`report-row-${r.id}`}>
                <td className="py-2 px-4">{r.name}</td>
                <td className="py-2 px-4">{r.frequency}</td>
                <td className="py-2 px-4 text-[11px]">{(r.recipient_emails || []).join(", ")}</td>
                <td className="py-2 px-4 text-[11px]">{fmtDate(r.next_run_at)}</td>
                <td className="py-2 px-4 text-[11px]">{r.last_run_at ? fmtDate(r.last_run_at) : "—"}</td>
                <td className="py-2 px-4">{r.last_status || "—"}</td>
                <td className="py-2 px-4">
                  <button onClick={() => toggle(r)} data-testid={`report-toggle-${r.id}`}
                    className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border ${r.is_active ? "border-black bg-black text-white" : "border-zinc-500 text-zinc-500"}`}>
                    {r.is_active ? "on" : "off"}
                  </button>
                </td>
                <td className="py-2 px-4 whitespace-nowrap">
                  <button onClick={() => runNow(r.id)} data-testid={`report-run-${r.id}`}
                    className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white mr-1">
                    <PaperPlaneTilt size={10} className="inline" /> Run now
                  </button>
                  <button onClick={() => del(r.id)} data-testid={`report-delete-${r.id}`}
                    className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white">
                    <Trash size={10} className="inline" /> Delete
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="py-6 text-center label-mono">No scheduled reports</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
